import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
    useEvaluatedAttributes,
    View,
} from '@ea-lab/reactive-json';

/**
 * Context providing the retry callback to FileFieldRetry sub-components.
 * Value: (localId: string) => void
 */
export const FileFieldRetryContext = createContext(null);

/**
 * Context providing the delete callback to FileFieldDelete sub-components.
 * Value: (fid: number, deleteUrl: string) => Promise<void>
 */
export const FileFieldDeleteContext = createContext(null);

let _localIdCounter = 0;
const nextLocalId = () => `_file_${++_localIdCounter}_${Date.now()}`;

/** Syncable Object keys that are metadata, i.e. never part of `data`. */
const SYNCABLE_METADATA_KEYS = [
    'item_url',
    'submission_url',
    'delete_url',
    'data',
    'status',
    '_localId',
    '_canRetry',
];

/**
 * Normalises an API file item into the Syncable Object shape { …urls, data, status }.
 *
 * The decoupled API only wraps an entity into `data` when the current user has
 * write access on it. Core grants file update access to the file owner only, so
 * the very same attached file comes back wrapped for its uploader (and admins)
 * but FLAT for everyone else — which used to render an empty row (no filename,
 * no preview, dead download link). Reading must not depend on write access.
 */
function toSyncableFile(item) {
    if (!item || typeof item !== 'object' || item.data) {
        return item;
    }
    const normalized = { data: {} };
    for (const [key, value] of Object.entries(item)) {
        if (SYNCABLE_METADATA_KEYS.includes(key)) {
            normalized[key] = value;
        }
        else {
            normalized.data[key] = value;
        }
    }
    return normalized;
}

/** Normalises a list of file items coming from the API. */
const toSyncableFiles = (items) =>
    (Array.isArray(items) ? items : []).map(toSyncableFile);

function formatBytes(bytes) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Default file item row rendered when no itemTemplate is configured.
 */
function DefaultFileItem({ item, retryUpload }) {
    const isError = item?.status?.type === 'error';
    const isUploading = item?.status?.type === 'info';

    return (
        <div>
            <span>{item?.data?.filename}</span>
            {item?.data?.filesize != null && (
                <span> ({formatBytes(item.data.filesize)})</span>
            )}
            {item?.status?.message && <span> — {item.status.message}</span>}
            {isUploading && <span aria-live="polite" aria-busy="true" />}
            {isError && item?._canRetry && (
                <button type="button" onClick={() => retryUpload(item._localId)}>
                    Retry
                </button>
            )}
        </div>
    );
}

/**
 * A reactive-json form field for file uploads.
 *
 * Supports drag-and-drop and OS file picker. Each successfully uploaded file
 * is stored as a Syncable Object in the global data array at `dataLocation`.
 * Pending and failed uploads live in local React state only.
 *
 * YAML configuration:
 *
 *   type: FileField
 *   dataLocation: ~~.taskFiles                    # array of syncable objects
 *   uploadUrl: /decoupled-api/file/upload/node/task/field_attached_files
 *   fidsLocation: ~~.taskSyncable.data.field_attached_files  # optional — writes [{ target_id }] for DataSync
 *   multiple: true                                 # optional, default true
 *   accept: "image/*,.pdf,.docx"                  # optional
 *   attributes:                                   # wrapper div
 *     class: flex flex-col gap-2
 *   dropzoneAttributes:                           # drop zone (idle)
 *     class: border-2 border-dashed border-gray-300 rounded-lg p-6 text-center
 *   dropzoneActiveAttributes:                     # drop zone (drag-over)
 *     class: border-2 border-dashed border-blue-500 rounded-lg p-6 text-center bg-blue-50
 *   buttonTemplate:                               # optional — replaces default button
 *     type: button
 *     content: "Browse files…"
 *   itemTemplate:                                 # optional — template data is the syncable object
 *     type: div
 *     content:
 *       - type: FileFieldPreview
 *       - type: span
 *         content: ~.data.filename
 *       - type: span
 *         content: ~.status.message
 *         actions:
 *           - what: hide
 *             when: ~.status.type
 *             is: success
 *       - type: FileFieldRetry   # only visible when ~._canRetry is true
 *
 * Template data available inside itemTemplate via ~.:
 *   ~.data.filename  ~.data.filesize  ~.data.filemime  ~.data.url  ~.data.fid
 *   ~.status.type    ~.status.message
 *   ~._localId       ~._canRetry   (only for pending/failed items)
 */
export const FileField = ({ props, path, datafield }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const attributes = useEvaluatedAttributes(props.attributes);
    const dropzoneAttrsIdle = useEvaluatedAttributes(props.dropzoneAttributes);
    const dropzoneAttrsActive = useEvaluatedAttributes(props.dropzoneActiveAttributes);

    const [isDragOver, setIsDragOver] = useState(false);
    const [pendingUploads, setPendingUploads] = useState([]);

    const inputRef = useRef(null);

    const { formData, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: props.defaultFieldValue ?? [],
        globalDataContext,
        templateContext,
    });

    // Keep a ref to the latest formData to avoid stale closures in uploadFile.
    const formDataRef = useRef(formData);
    useEffect(() => { formDataRef.current = formData; }, [formData]);

    // Optional secondary location for { target_id } references only.
    // Useful when a parent DataSync payload needs the fids (e.g. task creation).
    const { formData: fidsData, formDataPath: fidsPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.fidsLocation ?? null,
        defaultValue: [],
        globalDataContext,
        templateContext,
    });
    const fidsDataRef = useRef(fidsData);
    useEffect(() => { fidsDataRef.current = fidsData; }, [fidsData]);

    const uploadUrl = evaluateTemplateValue({
        valueToEvaluate: props.uploadUrl,
        globalDataContext,
        templateContext,
    }) || '';

    const entityId = evaluateTemplateValue({
        valueToEvaluate: props.entityId,
        globalDataContext,
        templateContext,
    }) || null;

    const multiple = props.multiple !== false;
    const accept = props.accept || undefined;

    const uploadFile = useCallback(async (file, localId) => {
        if (!uploadUrl) {
            setPendingUploads(prev =>
                prev.map(p => p.localId === localId
                    ? { ...p, status: 'error', error: 'uploadUrl is not configured.' }
                    : p)
            );
            return;
        }

        const effectiveUrl = entityId
            ? `${uploadUrl}${uploadUrl.includes('?') ? '&' : '?'}entity_id=${encodeURIComponent(entityId)}`
            : uploadUrl;

        try {
            const response = await fetch(effectiveUrl, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Disposition': `file; filename="${encodeURIComponent(file.name)}"`,
                },
                body: file,
            });

            let json;
            try {
                json = await response.json();
            } catch {
                throw new Error(`Unexpected response (HTTP ${response.status})`);
            }

            if (!response.ok || json?.status?.type === 'error') {
                const msg = json?.status?.message || `Upload failed (HTTP ${response.status})`;
                setPendingUploads(prev =>
                    prev.map(p => p.localId === localId ? { ...p, status: 'error', error: msg } : p)
                );
                return;
            }

            // Success: append syncable object to the display list.
            const uploaded = toSyncableFile(json);
            const current = toSyncableFiles(formDataRef.current);
            globalDataContext.updateData([...current, uploaded], formDataPath);

            // If fidsLocation is configured, also write the { target_id } reference
            // so a parent DataSync payload includes the fid (needed when the entity
            // does not exist yet and immediate attachment via entity_id is not possible).
            if (props.fidsLocation && fidsPath && uploaded?.data?.fid != null) {
                const currentFids = Array.isArray(fidsDataRef.current) ? fidsDataRef.current : [];
                globalDataContext.updateData(
                    [...currentFids, uploaded.data.fid],
                    fidsPath,
                );
            }

            setPendingUploads(prev => prev.filter(p => p.localId !== localId));
        } catch (err) {
            const msg = err?.message || 'Network error.';
            setPendingUploads(prev =>
                prev.map(p => p.localId === localId ? { ...p, status: 'error', error: msg } : p)
            );
        }
    }, [uploadUrl, formDataPath, fidsPath, props.fidsLocation, globalDataContext]);

    const handleFiles = useCallback((files) => {
        const fileList = Array.from(files);
        const newPending = fileList.map(file => ({
            localId: nextLocalId(),
            file,
            status: 'uploading',
            error: null,
        }));
        setPendingUploads(prev => [...prev, ...newPending]);
        newPending.forEach(({ file, localId }) => uploadFile(file, localId));
    }, [uploadFile]);

    const retryUpload = useCallback((localId) => {
        const pending = pendingUploads.find(p => p.localId === localId);
        if (!pending || pending.status !== 'error') return;
        setPendingUploads(prev =>
            prev.map(p => p.localId === localId ? { ...p, status: 'uploading', error: null } : p)
        );
        uploadFile(pending.file, localId);
    }, [pendingUploads, uploadFile]);

    // Removes a saved file: detaches it from the parent entity, deletes the
    // file entity, then re-fetches to synchronise the display (same refresh
    // pattern as comments in TaskEdit).
    const removeFile = useCallback(async (fid, deleteUrl) => {
        // Optimistic removal from the display list. Normalise first: a flat
        // (unwrapped) item has no `data.fid`, so filtering on the raw list
        // would remove nothing and then rebuild an EMPTY fid list below.
        const current = toSyncableFiles(formDataRef.current);
        const remaining = current.filter(f => f.data?.fid !== fid);
        globalDataContext.updateData(remaining, formDataPath);

        try {
            const tokenRes = await fetch('/session/token', { credentials: 'include' });
            const csrfToken = tokenRes.ok ? await tokenRes.text() : '';
            const authHeaders = {
                'X-CSRF-Token': csrfToken,
                'Content-Type': 'application/json',
            };

            let entType, bundle, fieldName, apiKey;
            if (entityId && uploadUrl) {
                const parts = uploadUrl.replace(/^\/decoupled-api\/file\/upload\//, '').split('/');
                if (parts.length >= 3) {
                    [entType, bundle, fieldName] = parts;
                    apiKey = fieldName
                        .replace(/^field_/, '')
                        .replace(/_([a-z])/g, (_, c) => c.toUpperCase());
                }
            }

            // 1. Save the parent entity WITHOUT the deleted file reference.
            //    This removes the dangling reference in field_attached_files
            //    and invalidates the entity render cache.
            if (entType) {
                const remainingFids = remaining
                    .filter(f => f.data?.fid != null)
                    .map(f => f.data.fid);
                const saveUrl = `/decoupled-api/save?display=decoupled&entity_type=${entType}&bundle=${bundle}&entity_id=${entityId}`;
                await fetch(saveUrl, {
                    method: 'POST',
                    credentials: 'include',
                    headers: authHeaders,
                    body: JSON.stringify({ data: { [fieldName]: remainingFids } }),
                });
            }

            // 2. Delete the file entity itself.
            if (deleteUrl) {
                await fetch(deleteUrl, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'X-CSRF-Token': csrfToken },
                });
            }

            // 3. Re-fetch the parent entity to synchronise the display with
            //    the authoritative data (cache-busting timestamp).
            if (entType) {
                const entityUrl = `/decoupled-api/${entType}/${bundle}/${entityId}?display=decoupled&_=${Date.now()}`;
                const resp = await fetch(entityUrl, { credentials: 'include' });
                if (resp.ok) {
                    const json = await resp.json();
                    const freshFiles = json?.data?.[apiKey] ?? json?.[apiKey] ?? [];
                    globalDataContext.updateData(
                        toSyncableFiles(freshFiles),
                        formDataPath,
                    );
                }
            }
        } catch {
            // The file is already hidden from the UI.
        }
    }, [formDataPath, globalDataContext, entityId, uploadUrl]);

    const onInputChange = useCallback((e) => {
        if (e.target.files?.length) handleFiles(e.target.files);
        // Reset so the same file can be re-selected.
        e.target.value = '';
    }, [handleFiles]);

    const openFileDialog = useCallback(() => inputRef.current?.click(), []);

    const onDragOver = useCallback((e) => { e.preventDefault(); setIsDragOver(true); }, []);
    const onDragLeave = useCallback((e) => { e.preventDefault(); setIsDragOver(false); }, []);
    const onDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
    }, [handleFiles]);

    const dropzoneAttrs = isDragOver
        ? (dropzoneAttrsActive ?? dropzoneAttrsIdle ?? {})
        : (dropzoneAttrsIdle ?? {});

    const resolvedFiles = toSyncableFiles(formData);

    const pendingSyncableItems = pendingUploads.map(p => ({
        _localId: p.localId,
        _canRetry: p.status === 'error',
        data: {
            filename: p.file.name,
            filesize: p.file.size,
            filemime: p.file.type || 'application/octet-stream',
            fid: null,
            url: null,
            uri: null,
        },
        status: {
            type: p.status === 'uploading' ? 'info' : 'error',
            message: p.status === 'uploading' ? 'Uploading…' : (p.error || 'Upload failed.'),
        },
    }));

    const allItems = [...resolvedFiles, ...pendingSyncableItems];

    return (
        <ActionDependant {...props}>
            <FileFieldRetryContext.Provider value={retryUpload}>
            <FileFieldDeleteContext.Provider value={removeFile}>
                <div {...attributes}>
                    {/* Hidden file input */}
                    <input
                        ref={inputRef}
                        type="file"
                        multiple={multiple}
                        accept={accept}
                        style={{ display: 'none' }}
                        onChange={onInputChange}
                        aria-hidden="true"
                        tabIndex={-1}
                    />

                    {/* Drop zone — also the click target when no buttonTemplate */}
                    <div
                        {...dropzoneAttrs}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                    >
                        {props.buttonTemplate ? (
                            // Wrap in a click-capturing div so the template triggers the dialog.
                            <div onClick={openFileDialog} style={{ cursor: 'pointer' }}>
                                <View
                                    props={props.buttonTemplate}
                                    path={path + '.buttonTemplate'}
                                    datafield="buttonTemplate"
                                />
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={openFileDialog}
                            >
                                Drop files here or click to browse
                            </button>
                        )}
                    </div>

                    {/* File list */}
                    {allItems.map((item, index) => {
                        const itemKey = item?.data?.fid ?? item?._localId ?? index;
                        const itemPath = path + '.files.' + index;
                        return (
                            <TemplateContext.Provider
                                key={itemKey}
                                value={{ templateData: item, templatePath: itemPath }}
                            >
                                {props.itemTemplate ? (
                                    <View
                                        props={props.itemTemplate}
                                        path={itemPath}
                                        datafield={'files.' + index}
                                        currentData={item}
                                    />
                                ) : (
                                    <DefaultFileItem item={item} retryUpload={retryUpload} />
                                )}
                            </TemplateContext.Provider>
                        );
                    })}
                </div>
            </FileFieldDeleteContext.Provider>
            </FileFieldRetryContext.Provider>
        </ActionDependant>
    );
};
